import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSpaces, useCreateSpace } from '@/hooks';
import { Card, CardContent, Button, Skeleton, Input, Label } from '@/components/ui';
import { useAuthStore } from '@/stores';
import { Plus, Users, FileText, X } from 'lucide-react';
import toast from 'react-hot-toast';

export function Spaces() {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useSpaces(page, 10);
  const { user } = useAuthStore();
  const canCreate = user && (user.role === 'editor' || user.role === 'admin');

  return (
    <div className="max-w-4xl mx-auto py-12 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">我的空间</h1>
        {canCreate && <CreateSpaceButton />}
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : !data?.items?.length ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground mb-4">暂无空间</p>
            {canCreate && <CreateSpaceButton />}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {data.items.map((space) => (
            <Link key={space.id} to={`/spaces/${space.slug}`}>
              <Card className="hover:border-primary transition-colors">
                <CardContent className="py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h2 className="text-lg font-semibold hover:text-primary">
                        {space.name}
                      </h2>
                      {space.description && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {space.description}
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          {space.member_count} 成员
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText className="h-4 w-4" />
                          {space.page_count} 页面
                        </span>
                        <span className="text-xs px-2 py-0.5 bg-muted rounded">
                          {space.user_role === 'admin' ? '管理员' : space.user_role === 'member' ? '成员' : '访客'}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {data && data.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" disabled={page === 1} onClick={() => setPage(page - 1)}>
            上一页
          </Button>
          <span className="py-2 px-4 text-sm text-muted-foreground">
            第 {page} / {data.totalPages} 页
          </span>
          <Button variant="outline" disabled={page === data.totalPages} onClick={() => setPage(page + 1)}>
            下一页
          </Button>
        </div>
      )}
    </div>
  );
}

function CreateSpaceButton() {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const createSpace = useCreateSpace();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('请输入空间名称');
      return;
    }

    createSpace.mutate(
      { name: name.trim(), description: description.trim() || undefined },
      {
        onSuccess: () => {
          toast.success('空间创建成功');
          setShowForm(false);
          setName('');
          setDescription('');
        },
        onError: (error: any) => {
          toast.error(error.message || '创建失败');
        },
      }
    );
  };

  if (!showForm) {
    return (
      <Button onClick={() => setShowForm(true)}>
        <Plus className="h-4 w-4 mr-2" />
        创建空间
      </Button>
    );
  }

  return (
    <Card className="w-full max-w-md">
      <CardContent className="pt-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">创建新空间</h3>
            <Button type="button" variant="ghost" size="sm" onClick={() => setShowForm(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="space-y-2">
            <Label htmlFor="space-name">名称</Label>
            <Input
              id="space-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="空间名称"
              maxLength={100}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="space-desc">描述（可选）</Label>
            <Input
              id="space-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="简短描述"
              maxLength={500}
            />
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={createSpace.isPending}>
              {createSpace.isPending ? '创建中...' : '创建'}
            </Button>
            <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
              取消
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
